import type { Threshold, ThresholdInput } from '@painpal/contracts';
import { Pencil, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCreateThreshold, useDeleteThreshold, useUpdateThreshold } from '../../api/mutations';
import { useThresholds } from '../../api/queries';
import { ConfirmButton } from '../../components/ConfirmButton';
import { EmptyState, ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { errorMessage } from '../../lib/api';
import { formatDayMonthYear } from '../../lib/format';
import { ThresholdForm } from './ThresholdForm';
import {
  coveredLabel, currentThreshold, formatThresholdValue, sortAscending, sourceLabel, thresholdRowMeta, type ThresholdCopy,
} from './thresholdModel';
import styles from './settings.module.css';

export type Editing = { mode: 'new' } | { mode: 'edit'; id: string } | null;
const errorText = (error: Error | null): string | null => (error === null ? null : errorMessage(error));

interface RowProps { copy: ThresholdCopy; threshold: Threshold; current: boolean; onEdit: () => void; onDelete: () => void; deleting: boolean }

function ThresholdRow({ copy, threshold, current, onEdit, onDelete, deleting }: RowProps) {
  const value = formatThresholdValue(copy, threshold.value);
  const source = sourceLabel(threshold);
  return (
    <tr>
      <td data-cell="from">
        {formatDayMonthYear(threshold.effectiveFrom)}
        {source !== null && <span className={styles.source}>{`FROM ${source}`}</span>}
      </td>
      <td data-cell="value">
        <span className={styles.valueLine}>
          <span className={styles.valueText}>{value}</span>
          {current && <span className={styles.current}>CURRENT</span>}
        </span>
        <span data-part="meta" className={styles.meta}>{thresholdRowMeta(copy, threshold)}</span>
      </td>
      <td data-cell="rides">{coveredLabel(copy, threshold)}</td>
      <td className={styles.actions}>
        <button type="button" className={styles.edit} onClick={onEdit}>
          <Pencil size={16} aria-hidden="true" className={styles.icon} />
          <span data-part="label" className="sr-only-phone">EDIT</span>
        </button>
        <ConfirmButton
          label={<><Trash2 size={16} aria-hidden="true" className={styles.icon} /><span data-part="label" className="sr-only-phone">DELETE</span></>}
          confirmLabel="YES, DELETE" prompt={`DELETE ${value}?`} className={styles.deleteLink} busy={deleting} onConfirm={onDelete}
        />
      </td>
    </tr>
  );
}

interface TableProps { copy: ThresholdCopy; items: Threshold[]; today: string; editing: Editing; setEditing: (e: Editing) => void }

function ThresholdTable({ copy, items, today, editing, setEditing }: TableProps) {
  const update = useUpdateThreshold(copy.kind, copy.sport);
  const remove = useDeleteThreshold(copy.kind, copy.sport);
  const currentId = currentThreshold(items, today)?.id;
  const sorted = sortAscending(items).reverse();
  const save = (id: string) => (input: ThresholdInput): void => update.mutate({ id, input }, { onSuccess: () => setEditing(null) });
  const cancel = (): void => { update.reset(); setEditing(null); };
  return (
    <>
      {remove.error !== null && <p role="alert" className={styles.tableError}>{errorMessage(remove.error)}</p>}
      <table className={`data-table ${styles.table}`}>
        <thead className="sr-only-phone"><tr><th scope="col">VALID FROM</th><th scope="col">{copy.short}</th><th scope="col">{`${copy.covered[1]} COVERED`}</th><th scope="col"><span className="sr-only">ACTIONS</span></th></tr></thead>
        <tbody>
          {sorted.map((t) => (editing?.mode === 'edit' && editing.id === t.id ? (
            <tr key={t.id}><td colSpan={4}>
              <ThresholdForm copy={copy} initial={t} today={today} busy={update.isPending} error={errorText(update.error)} onSubmit={save(t.id)} onCancel={cancel} />
            </td></tr>
          ) : (
            <ThresholdRow key={t.id} copy={copy} threshold={t} current={t.id === currentId} deleting={remove.isPending} onEdit={() => { update.reset(); setEditing({ mode: 'edit', id: t.id }); }} onDelete={() => remove.mutate(t.id)} />
          )))}
        </tbody>
      </table>
    </>
  );
}

export interface ThresholdHistoryProps {
  copy: ThresholdCopy;
  today: string;
  editing: Editing;
  setEditing: (e: Editing) => void;
  summary: (items: Threshold[]) => ReactNode;
}

export function ThresholdHistory({ copy, today, editing, setEditing, summary }: ThresholdHistoryProps) {
  const thresholds = useThresholds(copy.kind, copy.sport);
  const create = useCreateThreshold(copy.kind, copy.sport);
  if (thresholds.isPending) return <LoadingBlock label={`LOADING ${copy.short} HISTORY`} />;
  if (thresholds.isError) return <ErrorPanel error={thresholds.error} onRetry={() => void thresholds.refetch()} />;
  const items = thresholds.data.items;
  const current = currentThreshold(items, today);
  return (
    <>
      {summary(items)}
      {editing?.mode === 'new' && (
        <ThresholdForm
          copy={copy} initial={{ value: current?.value ?? copy.fallback, effectiveFrom: today }} today={today} busy={create.isPending}
          error={errorText(create.error)} onCancel={() => { create.reset(); setEditing(null); }}
          onSubmit={(input) => create.mutate({ sport: copy.sport, kind: copy.kind, ...input }, { onSuccess: () => setEditing(null) })}
        />
      )}
      {items.length === 0 && editing === null && (
        <EmptyState title={`NO ${copy.short} YET`} body={copy.emptyBody}>
          <button type="button" className="btn btn-ink" onClick={() => setEditing({ mode: 'new' })}>{`+ ADD YOUR ${copy.short}`}</button>
        </EmptyState>
      )}
      {items.length > 0 && <ThresholdTable copy={copy} items={items} today={today} editing={editing} setEditing={setEditing} />}
    </>
  );
}
